import React, { useContext, useEffect, useState } from 'react'
import { useHistory, useParams } from 'react-router-dom'
import { toast, ToastContainer } from 'react-toastify'
import styles from './MultiPosePackageBuilder.module.sass'
import useQueryString from '../../../../hooks/use-query-string'
import FavoritePoseSelection from './steps/FavoritePoseSelection'
import OptionSelectionContent from '../../OptionSelection/OptionSelectionContent'
import useBackgroundsFromPackage from '../../package-builder/use-backgrounds-from-package'
import MultiPoseBuilder from './steps/MultiPoseBuilder'
import { routes } from '../../../../routes'
import useImageTypesFromOption from '../../package-builder/use-image-types-from-option'
import useOption from '../../package-builder/use-option'
import usePackage from '../../package-builder/use-package'
import { usePoses } from './hooks'
import { OrderPackage, Pose } from '../../../../models'
import { CustomerContext } from '../../../../contexts/CustomerContext'
import classes from '../../Sittings/Sittings.module.sass'
import Typography from '../../../Typography'
import useLocalStorage from '../../../../hooks/useLocalStorage'
import Addon from '../../../../models/Addon'
import * as Sentry from '@sentry/react'
import Button from "../../../Button";
import Grid from '../../../Grid';
import Sheet from '../../../../models/Sheet';

const NoImagesContent = () => {
  const history = useHistory();

  return (
    <div style={{ padding: '1em' }}>
      <Grid container justify='center' classes={[styles.maxWidth]} spacing={2}>
        <Grid item xs={8} md={4}>
        </Grid>
        <Typography align="center">
          No images are available yet for this student.
          Please check back tomorrow or call
          {' '}
          <a type="tel" href="209-567-0748">209-567-0748</a>
          {' '}
          for more information
          <Button fullWidth variant='submit' onClick={() => history.goBack()}>Return to Previous Page</Button>
        </Typography>
      </Grid>
    </div>
  )
}

const MultiPosePackageBuilder = () => {
  const { favorites: favoritesIds, opt, step, pastImages } = useQueryString()
  const { packageId } = useParams()
  const history = useHistory()
  const backgrounds = useBackgroundsFromPackage(packageId)
  const option = useOption(opt)
  const imageTypes = useImageTypesFromOption(opt, [favoritesIds].flat().length, false)
  const selectedPackage = usePackage(packageId)
  const [sheets, setSheets] = useState([])
  const [isComplete, setIsComplete] = useState(false)
  const { customer, setCustomer } = useContext(CustomerContext)
  const [error, setError] = useState(false)
  const [isUsingAccessCode] = useLocalStorage('isUsingAccessCode')
  const [hasAddonsAvailable, setHasAddonsAvailable] = useState(false)
  const [hasNoImages, setHasNoImages] = useState(false)

  useEffect(() => {
    Addon
      .where({ isElementary: true })
      .per(0)
      .stats({ total: 'count' })
      .all()
      .then((res) => setHasAddonsAvailable(res.meta.stats.total.count > 0))
  }, [])

  const { data: poseData, fetching } = usePoses(customer.currentStudent.id, [packageId], pastImages)

  useEffect(() => {
    setSheets([])
  }, [opt])

  useEffect(() => {
    if (fetching) return
    if (poseData[packageId].length > 0) return

    setHasNoImages(true)
    // history.push(routes.elementaryMultiPoseBuilder(packageId, { step: 'select-package', pastImages }))
  }, [fetching])

  const poses = poseData[packageId] || []

  const newFavoriteIds = [favoritesIds].flat().map((id) => id && id.toString())
  const favorites = poses.filter((pose) => newFavoriteIds.includes(pose.id))

  useEffect(() => {
    if (!opt) return;
    if (!option) return;
    if (!option.digitalImagesOnly) return;
    if (!imageTypes || imageTypes.length < 1) return;

    const sheetsFromFavorites = favorites.map((favorite, i) => {
      return new Sheet({ poseId: favorite.id, pose: favorite, imageType: imageTypes[0], imageTypeId: imageTypes[0].id, index: i });
    });

    setSheets(() => sheetsFromFavorites);
  }, [option && option.digitalImagesOnly, favorites.length, imageTypes && imageTypes.length]);

  useEffect(() => {
    if (!isComplete && sheets.length === 0) return

    if (sheets.filter((sheet) => !sheet.imageTypeId).length > 0) {
      Sentry.captureMessage(`No image type id for ${customer.cart && customer.cart.id}`);
    }

    const setOrderPackage = async () => {
      const potentialExtraPoses = [...new Set(sheets.map((sheet) => sheet.poseId))].length
      const extraPoses = option.digitalImagesOnly ? 0 : Math.max(0, potentialExtraPoses - option.poses)

      const orderPackage = new OrderPackage({
        cartId: customer.cart.id,
        studentId: customer.currentStudent.id,
        optionId: option.id,
        packageId,
        apiOption: option,
        option,
        extraPoses,
        sheets,
      })

      orderPackage
        .save({ with: 'sheets' })
        .then(() => {
          setCustomer({ ...customer, currentOrderPackage: orderPackage })

          const nextRoute = hasAddonsAvailable ? routes.multiPoseAddons({ favs: favoritesIds, op: orderPackage.id, opt: option.id, package: packageId, isSenior: false }) : routes.multiPoseReview(orderPackage.id)
          history.push(nextRoute)
        })
        .catch(() => {
          setError(true)
          toast('There was an error adding this item to your cart. Please refresh and try again.', { type: 'error' })
        })
    }

    setOrderPackage()
  }, [isComplete])

  useEffect(() => {
    if (fetching) return

    if (poses.length === 1 || !isUsingAccessCode) {
      // history.push(routes.elementaryMultiPoseBuilder(packageId, { favorites: poses.map((pose) => pose.id), step: 'select-package', pastImages }))
    }
  }, [fetching])

  if (hasNoImages) return <NoImagesContent />
  if (!backgrounds) return null
  if (fetching) return null

  const checkBackgrounds = favorites.some((favorite) => favorite.extension.includes('png')) || favorites.length === 0

  const background = backgrounds.length > 0 ? backgrounds[0].imageUrl : ''

  return (
    <div className={styles.container}>
      <ToastContainer position="bottom-left" />
      {(!step || step === 'pose-selection') && isUsingAccessCode && poses && (
        <FavoritePoseSelection backgrounds={backgrounds} background={background} images={poses} />
      )}
      {step === 'select-package' && (favorites.length > 0 || (selectedPackage)) && (
        <>
          <OptionSelectionContent
            foreground={favorites.length > 0 ? favorites[0].watermarkUrl : selectedPackage.placeholderImage()}
            background={background}
            multiPoseHandler={(selectedOpt) => history.push(routes.elementaryMultiPoseBuilder(packageId, { favorites: favoritesIds, opt: selectedOpt.id, step: 'build' }))}
            packageId={packageId}
            accessCodeOnly={false}
          />
        </>
      )}
      {step === 'build' && backgrounds && option && selectedPackage && imageTypes && !isComplete && (
        <MultiPoseBuilder
          placeholder={selectedPackage.placeholderImage()}
          checkBackgrounds={checkBackgrounds}
          images={favorites}
          backgrounds={backgrounds}
          option={option}
          selectedPackage={selectedPackage}
          imageTypes={imageTypes}
          prefilledSheets={sheets}
          onComplete={(newSheets) => {
            setSheets(newSheets)
            setIsComplete(true)
          }}
          checkForeground={poses.length > 0}
        />
      )}
      {isComplete && !error && <div>Generating your package...</div>}
      {error && (
        <div>
          There was a problem generating your package. Please refresh the page and try again.
        </div>
      )}
    </div>
  )
}

export default MultiPosePackageBuilder
